import { FC, useState } from 'react';
import { IFeatureProps } from 'mod/featureV2/interface';
import { FeatureBanner } from 'mod/featureV2/FeatureBanner';
import { Banner } from 'mod/featureV2/Banner';
import { forceVideoAsGif } from 'mod/featureV2/Banner/utils';
import { IAnchor, IAnchorsBanner, IFeatureAnchorsBanner } from 'mod/featureV2/interface/anchorsBanner';
import { LoopRender } from '@base/snippets/LoopRender';
import { ProductDetailPopup } from '@app/common/ProductDetailPopup';
import { getPercentage } from 'base/utils/dom-size';
import { Popover } from 'antd';
import { HtmlRender } from '@base/snippets/HtmlRender';
import { Button } from '@base/lib/Button';
import { isPhone } from 'base/utils/client';
import { IconArrow2 } from '@base/icon';
import { getTextColorClass } from 'mod/featureV2/utils';
import cn from 'classnames';

export type IProps = IFeatureProps<IFeatureAnchorsBanner> & {
    containerWidth?: number;
    popoverClassName?: string;
};

// 根据坐标位置排序，先上后下，先左后右
function sortAnchors(anchors: IAnchor[]): IAnchor[] {
    let sorted = anchors.sort((a, b) => {
        if (a.y === b.y) {
            return a.x - b.x;
        }
        return a.y - b.y;
    });

    let sort = 0;
    return sorted.map(anchor => {
        if (anchor.type !== 'NOTE') {
            return anchor;
        }
        return {
            ...anchor,
            note: {
                ...anchor.note,
                sort: sort++
            }
        }
    })
}

export const FeatureAnchorsBanner: FC<IProps> = props => {
    const [visible, setVisible] = useState<boolean>(false);
    const [variantId, setVariantId] = useState<string>();

    const {
        feature,
        popoverClassName,
        ...rest
    } = props;

    const { width, height, bgColor } = feature.config;

    return (
        <>
            <FeatureBanner<IAnchorsBanner>
                {...rest}
                feature={feature}
                renderBanner={bannerProps => {
                    const { banner: _banner, ...rest } = bannerProps;
                    let { anchors = [], ...banner } = _banner;
                    anchors = anchors.filter(anchor => anchor.x > 0 && anchor.y > 0 && anchor.x < width && anchor.y < height);


                    if (!anchors?.length) {
                        return <Banner {...rest} banner={banner}/>;
                    }

                    anchors = sortAnchors(anchors);

                    return (
                        <div className={'relative overflow'}>
                            <Banner
                                {...rest}
                                banner={{
                                    ...forceVideoAsGif(banner),
                                    href: void 0
                                }}
                            />
                            <LoopRender
                                tag={false}
                                list={anchors}
                                render={anchor => {
                                    switch (anchor.clickType) {
                                        case 'QUICK_VIEW':
                                            return !anchor.variantId ? null : (
                                                <div
                                                    className={'g-anchor'}
                                                    style={{
                                                        left: getPercentage(anchor.x, feature.config.width),
                                                        top: getPercentage(anchor.y, feature.config.height),
                                                    }}
                                                    onClick={() => {
                                                        setVariantId(anchor.variantId);
                                                        setVisible(true);
                                                    }}
                                                />
                                            );
                                        case 'BUBBLE':
                                            const { bubble } = anchor;
                                            return !bubble ? null : (
                                                <Popover
                                                    overlayClassName={popoverClassName}
                                                    placement={bubble.placement}
                                                    showArrow={false}
                                                    content={
                                                        <div>
                                                            <HtmlRender
                                                                className={'g-tip'}
                                                                html={bubble.tip}
                                                            />
                                                            {!!bubble.button && (
                                                                <Button {...bubble.button}>
                                                                    {bubble.button?.text}
                                                                </Button>
                                                            )}
                                                        </div>
                                                    }
                                                    trigger="click"
                                                    overlayStyle={{ maxWidth: bubble.width || (isPhone ? 240 : 383) }}
                                                >
                                                    <div
                                                        className={'g-anchor'}
                                                        style={{
                                                            left: getPercentage(anchor.x, feature.config.width),
                                                            top: getPercentage(anchor.y, feature.config.height),
                                                        }}
                                                    />
                                                </Popover>
                                            )
                                        case 'NOTE':
                                            const { note } = anchor;
                                            if (!note) {
                                                return null;
                                            }

                                            const colorClass = !note.style?.color
                                                ? getTextColorClass(note.style?.backgroundColor || banner.bgColor || bgColor)
                                                : '';

                                            return (
                                                <div
                                                    className={cn('g-note', colorClass)}
                                                    style={{
                                                        left: getPercentage(anchor.x, feature.config.width),
                                                        top: getPercentage(anchor.y, feature.config.height),
                                                        ...note.style,
                                                    }}
                                                    data-aos={'fade-up'}
                                                    data-aos-delay={note.sort * 200}
                                                    data-aos-once={true}
                                                >
                                                    <HtmlRender
                                                        className={'g-title'}
                                                        html={note.title}
                                                    />
                                                    <HtmlRender
                                                        className={'g-desc'}
                                                        html={note.description}
                                                    />
                                                    {!!note.button && (
                                                        <Button
                                                            icon={<IconArrow2 direction={'right'}/>}
                                                            className={'flex-reverse'}
                                                            shape={'round'}
                                                            size={'small'}
                                                            type={'primary'}
                                                            {...note.button}
                                                        >
                                                            {note.button?.text}
                                                        </Button>
                                                    )}
                                                </div>
                                            )
                                        default:
                                            return null;
                                    }
                                }}
                            />
                        </div>
                    )
                }}
            />

            {!!variantId && (
                <ProductDetailPopup
                    visible={visible}
                    onClose={() => setVisible(false)}
                    variantId={variantId}
                    pageFrom={feature.moduleId || 'anchorsBanner'}
                />
            )}
        </>
    )
};